/**
 * Nurikabe Engine — PRNG + Generator + Solver
 * Ported from /home/cbarrero/PSICO_ACTIVA/nuriv2/js/
 */

export type CellState = -1 | 0 | 1; // -1: unknown, 0: wall (black), 1: island (white)
export type Grid = CellState[][];
export type Clues = Record<string, number>; // "r,c" → size

export interface NurikabePuzzleData {
  width: number;
  height: number;
  clues: Clues;
  solution: Grid;
  seed: number;
}

const LEVELS: Record<number, { size: number; attempts: number }> = {
  1: { size: 5,  attempts: 20  },
  2: { size: 7,  attempts: 50  },
  3: { size: 9,  attempts: 100 },
  4: { size: 12, attempts: 200 },
  5: { size: 15, attempts: 300 },
};

// ─── PRNG ────────────────────────────────────────────────────────────────────

class PRNG {
  private m = 0x80000000;
  private a = 1103515245;
  private c = 12345;
  private state: number;

  constructor(seed: number) {
    this.state = seed;
  }

  nextInt(): number {
    this.state = (this.a * this.state + this.c) % this.m;
    return this.state;
  }

  nextFloat(): number {
    return this.nextInt() / (this.m - 1);
  }

  range(min: number, max: number): number {
    return min + Math.floor(this.nextFloat() * (max - min));
  }
}

// ─── Solver ──────────────────────────────────────────────────────────────────

class NurikabeSolver {
  private solutions: Grid[] = [];
  private maxSolutions = 2;

  constructor(
    private width: number,
    private height: number,
    private clues: Clues,
  ) {}

  solve(): Grid[] {
    const grid: Grid = Array(this.height)
      .fill(0)
      .map(() => Array(this.width).fill(-1)) as Grid;

    for (const key in this.clues) {
      const [r, c] = key.split(',').map(Number);
      grid[r][c] = 1;
    }

    this.solutions = [];
    this.backtrack(grid);
    return this.solutions;
  }

  private backtrack(grid: Grid): void {
    if (this.solutions.length >= this.maxSolutions) return;

    const simplified = this.applyLogic(grid);
    if (!simplified) return;

    const [r, c] = this.findUnknown(simplified);
    if (r === -1) {
      if (this.isValidFinal(simplified)) this.solutions.push(simplified);
      return;
    }

    const gridWall = this.cloneGrid(simplified);
    gridWall[r][c] = 0;
    this.backtrack(gridWall);

    if (this.solutions.length >= this.maxSolutions) return;

    const gridIsland = this.cloneGrid(simplified);
    gridIsland[r][c] = 1;
    this.backtrack(gridIsland);
  }

  private applyLogic(grid: Grid): Grid | null {
    return this.checkPartialValidity(grid) ? grid : null;
  }

  private checkPartialValidity(grid: Grid): boolean {
    // No 2×2 walls
    for (let r = 0; r < this.height - 1; r++) {
      for (let c = 0; c < this.width - 1; c++) {
        if (
          grid[r][c] === 0 && grid[r + 1][c] === 0 &&
          grid[r][c + 1] === 0 && grid[r + 1][c + 1] === 0
        ) return false;
      }
    }

    const regions = this.getIslandRegions(grid);
    for (const region of regions) {
      let clueVal: number | null = null;
      let clueCount = 0;
      for (const cell of region) {
        const v = this.clues[`${cell.r},${cell.c}`];
        if (v) { clueVal = v; clueCount++; }
      }
      if (clueCount > 1) return false;
      if (clueVal !== null) {
        if (region.length > clueVal) return false;
        if (this.isRegionClosed(region, grid) && region.length !== clueVal) return false;
      } else {
        if (this.isRegionClosed(region, grid)) return false;
      }
    }
    return true;
  }

  private isValidFinal(grid: Grid): boolean {
    // No 2×2 walls
    for (let r = 0; r < this.height - 1; r++) {
      for (let c = 0; c < this.width - 1; c++) {
        if (
          grid[r][c] === 0 && grid[r + 1][c] === 0 &&
          grid[r][c + 1] === 0 && grid[r + 1][c + 1] === 0
        ) return false;
      }
    }

    // Wall connectivity
    const wallCells: { r: number; c: number }[] = [];
    for (let r = 0; r < this.height; r++)
      for (let c = 0; c < this.width; c++)
        if (grid[r][c] === 0) wallCells.push({ r, c });

    if (wallCells.length > 0) {
      const visited = new Set<string>();
      const queue = [wallCells[0]];
      visited.add(`${wallCells[0].r},${wallCells[0].c}`);
      let count = 0;
      while (queue.length > 0) {
        const cur = queue.shift()!;
        count++;
        for (const [dr, dc] of [[0,1],[0,-1],[1,0],[-1,0]] as [number,number][]) {
          const nr = cur.r + dr, nc = cur.c + dc;
          const key = `${nr},${nc}`;
          if (nr >= 0 && nr < this.height && nc >= 0 && nc < this.width &&
              grid[nr][nc] === 0 && !visited.has(key)) {
            visited.add(key); queue.push({ r: nr, c: nc });
          }
        }
      }
      if (count !== wallCells.length) return false;
    }

    // Islands: each must have exactly one clue with matching size
    const regions = this.getIslandRegions(grid);
    const expected = Object.keys(this.clues).length;
    let found = 0;
    for (const region of regions) {
      let clueVal: number | null = null;
      let cCount = 0;
      for (const cell of region) {
        const v = this.clues[`${cell.r},${cell.c}`];
        if (v) { clueVal = v; cCount++; }
      }
      if (cCount !== 1 || region.length !== clueVal) return false;
      found++;
    }
    return found === expected;
  }

  private getIslandRegions(grid: Grid): { r: number; c: number }[][] {
    const visited = new Set<string>();
    const regions: { r: number; c: number }[][] = [];
    for (let r = 0; r < this.height; r++) {
      for (let c = 0; c < this.width; c++) {
        if (grid[r][c] === 1 && !visited.has(`${r},${c}`)) {
          const region: { r: number; c: number }[] = [];
          this.flood(grid, r, c, visited, region);
          regions.push(region);
        }
      }
    }
    return regions;
  }

  private flood(
    grid: Grid, r: number, c: number,
    visited: Set<string>, region: { r: number; c: number }[],
  ): void {
    const key = `${r},${c}`;
    if (visited.has(key)) return;
    visited.add(key);
    region.push({ r, c });
    for (const [dr, dc] of [[0,1],[0,-1],[1,0],[-1,0]] as [number,number][]) {
      const nr = r + dr, nc = c + dc;
      if (nr >= 0 && nr < this.height && nc >= 0 && nc < this.width && grid[nr][nc] === 1)
        this.flood(grid, nr, nc, visited, region);
    }
  }

  private isRegionClosed(region: { r: number; c: number }[], grid: Grid): boolean {
    for (const cell of region)
      for (const [dr, dc] of [[0,1],[0,-1],[1,0],[-1,0]] as [number,number][]) {
        const nr = cell.r + dr, nc = cell.c + dc;
        if (nr >= 0 && nr < this.height && nc >= 0 && nc < this.width && grid[nr][nc] === -1)
          return false;
      }
    return true;
  }

  private findUnknown(grid: Grid): [number, number] {
    for (let r = 0; r < this.height; r++)
      for (let c = 0; c < this.width; c++)
        if (grid[r][c] === -1) return [r, c];
    return [-1, -1];
  }

  private cloneGrid(grid: Grid): Grid {
    return grid.map(row => [...row]) as Grid;
  }
}

// ─── Generator ───────────────────────────────────────────────────────────────

export class NurikabeGenerator {
  private rng: PRNG = new PRNG(Date.now());

  async generate(difficulty: number, seed?: number): Promise<NurikabePuzzleData> {
    const numericSeed = seed ?? Math.floor(Math.random() * 2147483647);
    this.rng = new PRNG(numericSeed);

    const { size, attempts } = LEVELS[difficulty] ?? LEVELS[1];

    for (let i = 0; i < attempts; i++) {
      let fullGrid: number[][] | null = null;
      for (let j = 0; j < 20; j++) {
        fullGrid = this.generateStructure(size, size);
        if (fullGrid) break;
      }
      if (!fullGrid) continue;

      const clues = this.extractClues(fullGrid);
      const solutions = await this.runSolver(size, clues);

      if (solutions.length === 1) {
        return { width: size, height: size, clues, solution: solutions[0], seed: numericSeed };
      }
    }

    throw new Error('No se pudo generar un puzzle único. Intenta de nuevo.');
  }

  private runSolver(size: number, clues: Clues): Promise<Grid[]> {
    return new Promise(resolve =>
      setTimeout(() => resolve(new NurikabeSolver(size, size, clues).solve()), 0)
    );
  }

  private generateStructure(width: number, height: number): number[][] | null {
    const grid = Array(height).fill(0).map(() => Array(width).fill(0));
    const maxSteps = width * height * 3;

    for (let step = 0; step < maxSteps; step++) {
      const blocks = this.find2x2Walls(grid);
      if (blocks.length === 0) return this.checkWallConn(grid) ? grid : null;

      const block = blocks[Math.floor(this.rng.nextFloat() * blocks.length)];
      const cells = [
        { r: block.r, c: block.c }, { r: block.r + 1, c: block.c },
        { r: block.r, c: block.c + 1 }, { r: block.r + 1, c: block.c + 1 },
      ];
      for (let i = cells.length - 1; i > 0; i--) {
        const j = Math.floor(this.rng.nextFloat() * (i + 1));
        [cells[i], cells[j]] = [cells[j], cells[i]];
      }

      let ok = false;
      for (const cell of cells) {
        if (this.canBeIsland(grid, cell.r, cell.c)) { grid[cell.r][cell.c] = 1; ok = true; break; }
      }
      if (!ok) return null;
    }
    return null;
  }

  private find2x2Walls(grid: number[][]): { r: number; c: number }[] {
    const out: { r: number; c: number }[] = [];
    for (let r = 0; r < grid.length - 1; r++)
      for (let c = 0; c < grid[0].length - 1; c++)
        if (!grid[r][c] && !grid[r+1][c] && !grid[r][c+1] && !grid[r+1][c+1])
          out.push({ r, c });
    return out;
  }

  private canBeIsland(grid: number[][], r: number, c: number): boolean {
    let neighbors = 0;
    for (const [dr, dc] of [[0,1],[0,-1],[1,0],[-1,0]] as [number,number][]) {
      const nr = r + dr, nc = c + dc;
      if (nr >= 0 && nr < grid.length && nc >= 0 && nc < grid[0].length && grid[nr][nc] === 1)
        neighbors++;
    }
    if (neighbors > 1) return false;
    grid[r][c] = 1;
    const ok = this.checkWallConn(grid);
    grid[r][c] = 0;
    return ok;
  }

  private checkWallConn(grid: number[][]): boolean {
    let start: { r: number; c: number } | null = null;
    let total = 0;
    for (let r = 0; r < grid.length; r++)
      for (let c = 0; c < grid[0].length; c++)
        if (!grid[r][c]) { start = { r, c }; total++; }

    if (!total) return false;
    const visited = new Set<string>();
    const queue = [start!];
    visited.add(`${start!.r},${start!.c}`);
    let count = 0;
    while (queue.length > 0) {
      const cur = queue.shift()!; count++;
      for (const [dr, dc] of [[0,1],[0,-1],[1,0],[-1,0]] as [number,number][]) {
        const nr = cur.r + dr, nc = cur.c + dc, key = `${nr},${nc}`;
        if (nr >= 0 && nr < grid.length && nc >= 0 && nc < grid[0].length &&
            !grid[nr][nc] && !visited.has(key)) { visited.add(key); queue.push({ r: nr, c: nc }); }
      }
    }
    return count === total;
  }

  private extractClues(grid: number[][]): Clues {
    const clues: Clues = {};
    const visited = new Set<string>();
    for (let r = 0; r < grid.length; r++) {
      for (let c = 0; c < grid[0].length; c++) {
        if (grid[r][c] === 1 && !visited.has(`${r},${c}`)) {
          const region = this.getRegion(grid, r, c);
          region.forEach(sq => visited.add(`${sq.r},${sq.c}`));
          const pick = region[Math.floor(this.rng.nextFloat() * region.length)];
          clues[`${pick.r},${pick.c}`] = region.length;
        }
      }
    }
    return clues;
  }

  private getRegion(grid: number[][], r: number, c: number): { r: number; c: number }[] {
    const visited = new Set<string>([`${r},${c}`]);
    const queue = [{ r, c }];
    const cells: { r: number; c: number }[] = [];
    while (queue.length > 0) {
      const cur = queue.shift()!; cells.push(cur);
      for (const [dr, dc] of [[0,1],[0,-1],[1,0],[-1,0]] as [number,number][]) {
        const nr = cur.r + dr, nc = cur.c + dc, key = `${nr},${nc}`;
        if (nr >= 0 && nr < grid.length && nc >= 0 && nc < grid[0].length &&
            grid[nr][nc] === 1 && !visited.has(key)) { visited.add(key); queue.push({ r: nr, c: nc }); }
      }
    }
    return cells;
  }
}
